import type {
  AlertSeverity,
  TripAlertSummaryItem,
  TripAlertThresholds,
  TripForecastSummary,
  TripRecurrence,
  TripWatch,
  TripWatchPoint,
} from './types';

/** Pure logic for leave reminders and forecast-change alerts. No React Native imports (tested in node). */

export const MAX_WATCH_POINTS = 8;
/** Hysteresis so values hovering around a threshold don't spam notifications. */
export const RAIN_CLEAR_MARGIN_PCT = 10;
export const WIND_CLEAR_MARGIN_KMH = 8;
/** Only check trips that leave within this window (Open-Meteo hourly is reliable; avoids needless calls). */
export const CHECK_WINDOW_AHEAD_MS = 36 * 3600_000;
/** Don't send more than one change notification per trip in this period unless something escalates. */
export const MIN_NOTIFY_GAP_MS = 2 * 3600_000;
/** A forecast hour is usable for an ETA within this distance. */
export const MAX_HOUR_MATCH_MS = 90 * 60_000;

const SEVERITY_RANK: Record<AlertSeverity, number> = {
  Extreme: 4,
  Severe: 3,
  Moderate: 2,
  Minor: 1,
  Unknown: 0,
};

export function severityRank(s: AlertSeverity | string | undefined): number {
  return SEVERITY_RANK[(s as AlertSeverity) ?? 'Unknown'] ?? 0;
}

/** Pick up to `max` points evenly by index, always keeping first and last. */
export function thinWatchPoints<T>(points: T[], max = MAX_WATCH_POINTS): T[] {
  if (points.length <= max) return points.slice();
  if (max <= 1) return [points[0]!];
  const out: T[] = [];
  const step = (points.length - 1) / (max - 1);
  let lastIdx = -1;
  for (let i = 0; i < max; i++) {
    const idx = Math.round(i * step);
    if (idx !== lastIdx) out.push(points[idx]!);
    lastIdx = idx;
  }
  return out;
}

export function buildWatchPoints(
  waypoints: Array<{
    coordinate: { latitude: number; longitude: number };
    travelTimeFromStart: number;
    placeName?: string;
  }>,
  labels?: (index: number) => string | undefined,
): TripWatchPoint[] {
  const pts = waypoints
    .map((w, i) => ({
      latitude: w.coordinate.latitude,
      longitude: w.coordinate.longitude,
      offsetSec: Math.max(0, Math.round(w.travelTimeFromStart)),
      label: w.placeName ?? labels?.(i),
    }))
    .filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude));
  return thinWatchPoints(pts);
}

/** Next departure instant for a watch at/after `now` (ms). Returns null if a one-off trip already left. */
export function nextDeparture(watch: Pick<TripWatch, 'recurrence' | 'departAt' | 'durationSec'>, now: number): number | null {
  const r: TripRecurrence = watch.recurrence;
  if (r.kind === 'once') {
    return watch.departAt > now ? watch.departAt : null;
  }
  if (!r.weekdays.length) return null;
  const base = new Date(now);
  for (let add = 0; add <= 7; add++) {
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + add, r.hour, r.minute, 0, 0);
    if (!r.weekdays.includes(d.getDay())) continue;
    if (d.getTime() > now) return d.getTime();
  }
  return null;
}

/** When should the leave reminder fire for a one-off trip? null if it would be in the past. */
export function reminderFireAt(departAt: number, leadMin: number, now: number): number | null {
  const t = departAt - Math.max(0, leadMin) * 60_000;
  return t > now ? t : null;
}

/** Weekly reminder triggers (expo weekday: 1 = Sunday … 7 = Saturday) after subtracting the lead time. */
export function weeklyReminderTriggers(
  r: Extract<TripRecurrence, { kind: 'weekly' }>,
  leadMin: number,
): Array<{ weekday: number; hour: number; minute: number }> {
  const out: Array<{ weekday: number; hour: number; minute: number }> = [];
  const lead = Math.max(0, Math.round(leadMin));
  for (const wd of [...new Set(r.weekdays)].sort()) {
    let total = wd * 1440 + r.hour * 60 + r.minute - lead;
    total = ((total % 10080) + 10080) % 10080;
    const day = Math.floor(total / 1440);
    const rem = total % 1440;
    out.push({ weekday: day + 1, hour: Math.floor(rem / 60), minute: rem % 60 });
  }
  return out;
}

/** Should the background job check this watch now? */
export function shouldCheckWatch(watch: TripWatch, now: number): { check: boolean; departAt: number | null } {
  if (!watch.alertsEnabled || watch.points.length === 0) return { check: false, departAt: null };
  const dep = nextDeparture(watch, now);
  if (dep == null) return { check: false, departAt: null };
  // Keep checking while the trip is underway too (until arrival) — but nextDeparture only returns future;
  // a trip that has started is out of scope for "before you leave" alerts.
  return { check: dep - now <= CHECK_WINDOW_AHEAD_MS, departAt: dep };
}

export type PointHourly = {
  /** Unix ms per hour. */
  times: number[];
  precipProbability: (number | null)[];
  precipitationMm: (number | null)[];
  windGustsKmh: (number | null)[];
  temperatureC: (number | null)[];
  weatherCode: (number | null)[];
};

export function nearestHourIndex(times: number[], t: number): number {
  if (!times.length) return -1;
  let best = 0;
  let bestD = Math.abs(times[0]! - t);
  for (let i = 1; i < times.length; i++) {
    const d = Math.abs(times[i]! - t);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return bestD <= MAX_HOUR_MATCH_MS ? best : -1;
}

function dedupeAlerts(items: TripAlertSummaryItem[]): TripAlertSummaryItem[] {
  const seen = new Map<string, TripAlertSummaryItem>();
  for (const a of items) {
    const key = a.event.trim().toLowerCase();
    const prev = seen.get(key);
    if (!prev || severityRank(a.severity) > severityRank(prev.severity)) seen.set(key, a);
  }
  return [...seen.values()].sort((a, b) => severityRank(b.severity) - severityRank(a.severity));
}

/**
 * Summarise the forecast along a trip at each point's ETA and evaluate threshold flags.
 * `prev` enables hysteresis: a flag that was on stays on until the value drops below threshold − margin.
 */
export function summarizeTrip(params: {
  points: TripWatchPoint[];
  hourly: (PointHourly | null)[];
  alertsByPoint?: (TripAlertSummaryItem[] | null)[];
  departAt: number;
  thresholds: TripAlertThresholds;
  prev?: TripForecastSummary;
  now: number;
}): TripForecastSummary {
  const { points, hourly, departAt, thresholds, prev, now } = params;
  let maxPrecip: number | null = null;
  let maxPrecipLabel: string | undefined;
  let maxPrecipEta: number | undefined;
  let maxGust: number | null = null;
  let maxGustLabel: string | undefined;
  let maxGustEta: number | undefined;
  let minT: number | null = null;
  let maxT: number | null = null;
  let covered = 0;

  points.forEach((p, i) => {
    const h = hourly[i];
    if (!h) return;
    const eta = departAt + p.offsetSec * 1000;
    const idx = nearestHourIndex(h.times, eta);
    if (idx < 0) return;
    covered += 1;
    const pop = h.precipProbability[idx];
    const mm = h.precipitationMm[idx];
    // Treat measurable precipitation as at least moderately likely even if probability is missing.
    const effPop = pop ?? (mm != null && mm >= 0.5 ? 60 : null);
    if (effPop != null && (maxPrecip == null || effPop > maxPrecip)) {
      maxPrecip = effPop;
      maxPrecipLabel = p.label;
      maxPrecipEta = eta;
    }
    const g = h.windGustsKmh[idx];
    if (g != null && (maxGust == null || g > maxGust)) {
      maxGust = g;
      maxGustLabel = p.label;
      maxGustEta = eta;
    }
    const t = h.temperatureC[idx];
    if (t != null) {
      minT = minT == null ? t : Math.min(minT, t);
      maxT = maxT == null ? t : Math.max(maxT, t);
    }
  });

  const alerts = dedupeAlerts((params.alertsByPoint ?? []).flatMap((a) => a ?? []));
  const minRank = severityRank(thresholds.alertMinSeverity);

  const rainOnAt = thresholds.rainProbabilityPct;
  const rainOffBelow = rainOnAt - RAIN_CLEAR_MARGIN_PCT;
  const windOnAt = thresholds.windGustKmh;
  const windOffBelow = windOnAt - WIND_CLEAR_MARGIN_KMH;

  const mp = maxPrecip as number | null;
  const mg = maxGust as number | null;
  const rainy = mp == null ? !!prev?.rainy : prev?.rainy ? mp >= rainOffBelow : mp >= rainOnAt;
  const windy = mg == null ? !!prev?.windy : prev?.windy ? mg >= windOffBelow : mg >= windOnAt;
  const alerted = alerts.some((a) => severityRank(a.severity) >= minRank);

  return {
    checkedAt: now,
    departAt,
    maxPrecipPct: mp,
    maxPrecipLabel,
    maxPrecipEta,
    maxGustKmh: mg,
    maxGustLabel,
    maxGustEta,
    minTempC: minT,
    maxTempC: maxT,
    alerts,
    pointsCovered: covered,
    pointsTotal: points.length,
    rainy,
    windy,
    alerted,
  };
}

export type ChangeKind = 'rain-on' | 'rain-off' | 'wind-on' | 'wind-off' | 'alert-new' | 'alert-cleared';

export type ForecastChange = { kind: ChangeKind; detail?: string; severity?: AlertSeverity };

/**
 * Compare the previous and new summary. The first ever check establishes a baseline and only reports
 * risks that are already present (so the user hears about rain that's already forecast once).
 */
export function detectChanges(
  prev: TripForecastSummary | undefined,
  next: TripForecastSummary,
  thresholds: TripAlertThresholds,
): ForecastChange[] {
  const out: ForecastChange[] = [];
  // Not enough data → never claim things cleared.
  const reliable = next.pointsTotal === 0 ? false : next.pointsCovered / next.pointsTotal >= 0.5;
  if (!prev) {
    if (!reliable) return out;
    if (next.rainy) out.push({ kind: 'rain-on' });
    if (next.windy) out.push({ kind: 'wind-on' });
  } else if (prev.departAt !== next.departAt) {
    // New occurrence of a recurring trip: treat like a fresh baseline.
    if (reliable && next.rainy) out.push({ kind: 'rain-on' });
    if (reliable && next.windy) out.push({ kind: 'wind-on' });
  } else {
    if (!prev.rainy && next.rainy) out.push({ kind: 'rain-on' });
    if (!prev.windy && next.windy) out.push({ kind: 'wind-on' });
    if (thresholds.notifyOnClearing && reliable) {
      if (prev.rainy && !next.rainy) out.push({ kind: 'rain-off' });
      if (prev.windy && !next.windy) out.push({ kind: 'wind-off' });
    }
  }
  const minRank = severityRank(thresholds.alertMinSeverity);
  const prevEvents = new Map((prev?.departAt === next.departAt ? prev.alerts : []).map((a) => [a.event.toLowerCase(), a]));
  for (const a of next.alerts) {
    if (severityRank(a.severity) < minRank) continue;
    const before = prevEvents.get(a.event.toLowerCase());
    if (!before || severityRank(a.severity) > severityRank(before.severity)) {
      out.push({ kind: 'alert-new', detail: a.event, severity: a.severity });
    }
  }
  if (thresholds.notifyOnClearing && prev && prev.departAt === next.departAt && prev.alerted && !next.alerted) {
    out.push({ kind: 'alert-cleared' });
  }
  return out;
}

export function changeFingerprint(changes: ForecastChange[], departAt: number): string {
  return `${departAt}|${changes
    .map((c) => `${c.kind}:${c.detail ?? ''}:${c.severity ?? ''}`)
    .sort()
    .join(',')}`;
}

/** Rate-limit: allow if different from last fingerprint and (gap elapsed or an alert/escalation is included). */
export function shouldNotify(
  watch: Pick<TripWatch, 'lastNotifiedAt' | 'lastNotifiedKey'>,
  changes: ForecastChange[],
  departAt: number,
  now: number,
): boolean {
  if (!changes.length) return false;
  const key = changeFingerprint(changes, departAt);
  if (key === watch.lastNotifiedKey) return false;
  const urgent = changes.some((c) => c.kind === 'alert-new' && severityRank(c.severity) >= SEVERITY_RANK.Severe);
  if (urgent) return true;
  if (watch.lastNotifiedAt == null) return true;
  return now - watch.lastNotifiedAt >= MIN_NOTIFY_GAP_MS;
}

export type Formatters = {
  time: (ms: number) => string;
  /** Wind speed from km/h, with unit. */
  wind: (kmh: number) => string;
};

function near(label?: string): string {
  return label ? ` near ${label}` : '';
}

/** One-line weather summary for notifications, e.g. "Rain likely near Baker ~6:05 PM (70%) · gusts 25 mph". */
export function describeSummary(s: TripForecastSummary, thresholds: TripAlertThresholds, f: Formatters): string {
  const parts: string[] = [];
  if (s.maxPrecipPct != null && s.maxPrecipPct >= Math.min(30, thresholds.rainProbabilityPct)) {
    const word = s.maxPrecipPct >= thresholds.rainProbabilityPct ? 'Rain likely' : 'Chance of rain';
    parts.push(
      `${word}${near(s.maxPrecipLabel)}${s.maxPrecipEta ? ` ~${f.time(s.maxPrecipEta)}` : ''} (${Math.round(s.maxPrecipPct)}%)`,
    );
  }
  if (s.maxGustKmh != null && s.maxGustKmh >= thresholds.windGustKmh - WIND_CLEAR_MARGIN_KMH) {
    parts.push(`gusts ${f.wind(s.maxGustKmh)}${parts.length ? '' : near(s.maxGustLabel)}`);
  }
  const worst = s.alerts[0];
  if (worst && severityRank(worst.severity) >= severityRank(thresholds.alertMinSeverity)) {
    parts.push(s.alerts.length > 1 ? `${worst.event} +${s.alerts.length - 1} more` : worst.event);
  }
  if (!parts.length) {
    if (s.pointsCovered === 0) return 'Open the app for the latest forecast along your route.';
    return 'Looks clear along your route.';
  }
  const text = parts.join(' · ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function buildReminderContent(
  watch: Pick<TripWatch, 'destinationName' | 'thresholds' | 'lastSummary'>,
  departAt: number,
  f: Formatters,
): { title: string; body: string } {
  const title = `Time to leave for ${watch.destinationName}`;
  const wx = watch.lastSummary && watch.lastSummary.departAt === departAt
    ? describeSummary(watch.lastSummary, watch.thresholds, f)
    : 'Open the app for the latest forecast along your route.';
  return { title, body: `Leave ${f.time(departAt)}. ${wx}` };
}

export function buildChangeContent(
  watch: Pick<TripWatch, 'name' | 'thresholds'>,
  changes: ForecastChange[],
  summary: TripForecastSummary,
  f: Formatters,
): { title: string; body: string } {
  const kinds = new Set(changes.map((c) => c.kind));
  const alertNew = changes.find((c) => c.kind === 'alert-new');
  let title: string;
  if (alertNew) title = `${alertNew.detail ?? 'Weather alert'} on your route`;
  else if (kinds.has('rain-on') && kinds.has('wind-on')) title = 'Rain and strong wind now forecast';
  else if (kinds.has('rain-on')) title = 'Rain now forecast on your trip';
  else if (kinds.has('wind-on')) title = 'Strong wind now forecast on your trip';
  else title = 'Your trip forecast improved';
  const lead = `${watch.name} · leave ${f.time(summary.departAt)}. `;
  const body =
    kinds.has('rain-on') || kinds.has('wind-on') || alertNew
      ? describeSummary(summary, watch.thresholds, f)
      : changes
          .map((c) =>
            c.kind === 'rain-off'
              ? 'Rain no longer expected'
              : c.kind === 'wind-off'
                ? 'Wind has eased'
                : c.kind === 'alert-cleared'
                  ? 'Alerts along the route have ended'
                  : '',
          )
          .filter(Boolean)
          .join(' · ') + '.';
  return { title, body: lead + body };
}
